import { useCallback, useEffect, useState } from 'react';
import { useStoreActions } from '@/store';
import { useMutation } from '@apollo/react-hooks';
import type { MutationResult } from '@apollo/react-common';
import type { ApolloError } from 'apollo-client';

import { CI, features } from '@/lib';
import { useFilteredFormState } from '@/components/Form/hooks/useFilteredFormState';
import {
  approvePayment,
  // @ts-expect-error untyped export
  onAfterCheckoutGuestWithMemberBenefits,
  preAuthorizePaymentPlan,
} from '@/components/Form/graphql';
// @ts-expect-error untyped export
import { serializeDataForMemberBenefits } from '@/components/Form/util';
import { CHECKOUT_GUEST_WITH_MEMBER_BENEFITS_MUTATION } from '../memberBenefitsMutation.gql';
import type {
  CheckoutGuestWithMemberBenefitsMutationMutation,
  CheckoutGuestWithMemberBenefitsMutationMutationVariables,
} from '@/generated/graphql';
import { appStore } from '@/store';

const handleResponse = async (
  data: MutationResult<CheckoutGuestWithMemberBenefitsMutationMutation>,
) => {
  await onAfterCheckoutGuestWithMemberBenefits(data);

  const { user: userActions } = appStore.getActions();
  const need3DS =
    //@ts-expect-error
    //TODO:this should be data?.data?.checkoutGuestWithMemberBenefits
    data?.checkoutGuestWithMemberBenefits?.flags?.is3DSecureRequired;
  if (need3DS) {
    throw new Error('3DS_ERROR_IN_MEMBER_BENEFITS');
  } else {
    const isCPAEligible = features.config.CTAMessageTreatment !== 'CONTROL';
    userActions.setIsUserGuestWithMemberBenefits(true);
    if (isCPAEligible) {
      await preAuthorizePaymentPlan();
    } else {
      await approvePayment();
    }
  }
};

type UseCHeckoutAsMemberResponse = [
  () => void,
  {
    error: boolean | ApolloError;
    errorMessage: string | undefined;
    loading: boolean;
  },
];

/**
 * Checks out user as a member
 */
export const useCheckoutAsMember = (): UseCHeckoutAsMemberResponse => {
  // authorizeInFlight is used to keep loading state active until
  // preAuthorize and approve calls are complete or error
  const [authorizeInFlight, setAuthorizeInFlight] = useState(false);
  const [apiError, setApiError] = useState<string | false>(false);
  const setCheckForMemberBenefits = useStoreActions(
    store => store.ui.setCheckForMemberBenefits,
  );
  const filteredFormState = useFilteredFormState();

  const errorHandler = (err: ApolloError) => {
    const errMsg =
      err?.message || err?.name || 'UNKNOWN_ERROR_THROWN_FROM_APPOLLO_CLIENT';

    CI.endWith(
      CI.Event.MEMBER_MATCH_PAY_SUBMIT,
      CI.Status.Error,
      CI.ErrorReason.Server,
    );
    setApiError(errMsg);
    setCheckForMemberBenefits(false);
  };

  const [mutationFn, mutationResponse] = useMutation<
    CheckoutGuestWithMemberBenefitsMutationMutation,
    CheckoutGuestWithMemberBenefitsMutationMutationVariables
  >(CHECKOUT_GUEST_WITH_MEMBER_BENEFITS_MUTATION, {
    // Until we update our client we have to manually catch errors like this
    // https://github.com/apollographql/apollo-client/issues/6070
    onError: errorHandler,
  });

  const { data, error, loading } = mutationResponse;

  const isError = Boolean(error || apiError);
  const isLoading = loading || authorizeInFlight;

  useEffect(() => {
    if (data && !isLoading && !isError) {
      setAuthorizeInFlight(true);
      handleResponse(mutationResponse).catch(errorHandler);
    }
  }, [Boolean(data), isLoading, isError]);

  const commit = useCallback(() => {
    const variables = serializeDataForMemberBenefits(filteredFormState);
    mutationFn({ variables });
  }, [filteredFormState]);

  return [
    commit,
    {
      error: isError,
      errorMessage:
        apiError || error?.message || error?.graphQLErrors[0]?.message,
      loading: isLoading,
    },
  ];
};
